// AppSync-flavoured graphql-ws client for `@graphiql/toolkit`'s
// createGraphiQLFetcher({ wsClient }) option.
//
// AppSync's realtime endpoint speaks a non-standard graphql-ws flavour:
// start / start_ack / data / complete instead of subscribe / next / complete,
// with the auth payload base64url-encoded into the Sec-WebSocket-Protocol
// header. The stock `graphql-ws` package does not handle that, so we expose
// the subset of the graphql-ws Client interface that @graphiql/toolkit calls
// (`subscribe(payload, sink)` returning an unsubscribe fn, plus `dispose()`).

const base64UrlEncode = (text) =>
  btoa(text).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');

const STANDARD_APPSYNC_HOST_PATTERN =
  /^([a-z0-9-]+)\.appsync-api\.([a-z0-9-]+)\.amazonaws\.com$/;

const deriveRealtimeConfig = (httpsEndpoint) => {
  const url = new URL(httpsEndpoint);
  const host = url.host;
  const standardMatch = host.match(STANDARD_APPSYNC_HOST_PATTERN);
  if (standardMatch) {
    const [, apiId, region] = standardMatch;
    return {
      realtimeUrl: `wss://${apiId}.appsync-realtime-api.${region}.amazonaws.com/graphql`,
      httpsAuthHost: host,
    };
  }
  // Custom domain
  return {
    realtimeUrl: `wss://${host}/graphql/realtime`,
    httpsAuthHost: host,
  };
};

export const createAppsyncWsClient = ({ httpsEndpoint, getAuthToken }) => {
  const { realtimeUrl, httpsAuthHost } = deriveRealtimeConfig(httpsEndpoint);

  let socket;
  let connectPromise;
  let authPayload;
  const active = new Map(); // id -> { sink }

  const send = (frame) => socket?.send(JSON.stringify(frame));
  const clearConnection = (ws) => {
    if (socket !== ws) return;
    socket = undefined;
    connectPromise = null;
    authPayload = undefined;
  };

  const handleMessage = (raw) => {
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg.type === 'ka' || msg.type === 'connection_ack' || msg.type === 'start_ack') {
      return;
    }
    if (msg.type === 'data' && msg.id) {
      active.get(msg.id)?.sink.next(msg.payload);
      return;
    }
    if (msg.type === 'error' && msg.id) {
      const message = msg.payload?.errors?.map?.((e) => e.message).join('; ') ?? 'subscription error';
      active.get(msg.id)?.sink.error(new Error(message));
      return;
    }
    if (msg.type === 'complete' && msg.id) {
      const entry = active.get(msg.id);
      entry?.sink.complete?.();
      active.delete(msg.id);
    }
  };

  const ensureConnected = () => {
    if (connectPromise) return connectPromise;

    connectPromise = (async () => {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('AppSync realtime requires a Cognito ID token');
      }
      authPayload = { host: httpsAuthHost, Authorization: token };
      const subprotocol = `header-${base64UrlEncode(JSON.stringify(authPayload))}`;

      return new Promise((resolve, reject) => {
        const ws = new WebSocket(realtimeUrl, ['graphql-ws', subprotocol]);
        let connected = false;
        socket = ws;

        const failBeforeAck = (error) => {
          clearConnection(ws);
          reject(error);
        };

        ws.onopen = () => ws.send(JSON.stringify({ type: 'connection_init' }));
        ws.onmessage = (event) => {
          let parsed;
          try {
            parsed = JSON.parse(typeof event.data === 'string' ? event.data : '');
          } catch {
            return;
          }
          if (parsed.type === 'connection_ack') {
            connected = true;
            resolve();
            ws.onmessage = (next) =>
              handleMessage(typeof next.data === 'string' ? next.data : '');
            return;
          }
          if (parsed.type === 'connection_error') {
            const msg = parsed.payload?.errors?.[0]?.message ?? 'connection_error';
            failBeforeAck(new Error(msg));
          }
        };
        ws.onerror = () => failBeforeAck(new Error('AppSync realtime WebSocket error'));
        ws.onclose = (event) => {
          const error = new Error(`AppSync realtime closed (${event.code})`);
          clearConnection(ws);
          if (!connected) {
            reject(error);
            return;
          }
          for (const { sink } of active.values()) {
            sink.error?.(error);
          }
          active.clear();
        };
      });
    })().catch((error) => {
      if (!socket) {
        connectPromise = null;
        authPayload = undefined;
      }
      throw error;
    });

    return connectPromise;
  };

  return {
    /**
     * @param payload { query, variables, operationName, extensions? }
     * @param sink    { next(result), error(err), complete() }
     * @returns       unsubscribe function
     */
    subscribe(payload, sink) {
      const id =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `sub-${Math.random().toString(36).slice(2)}-${Date.now()}`;
      active.set(id, { sink });

      ensureConnected()
        .then(() => {
          if (!active.has(id)) {
            return;
          }
          send({
            id,
            type: 'start',
            payload: {
              data: JSON.stringify({
                query: payload.query,
                variables: payload.variables,
                operationName: payload.operationName,
              }),
              extensions: { authorization: authPayload },
            },
          });
        })
        .catch((err) => {
          if (active.delete(id)) {
            sink.error?.(err);
          }
        });

      return () => {
        if (active.delete(id) && socket && socket.readyState === WebSocket.OPEN) {
          send({ id, type: 'stop' });
        }
      };
    },
    dispose() {
      socket?.close();
      socket = undefined;
      connectPromise = null;
      active.clear();
    },
  };
};
